import { useState, useEffect, useRef, useContext, Suspense } from "react";
import ExperienceContext from "./ExperienceContext";
import { Canvas } from "@react-three/fiber";
import { TransformControls, useContextBridge } from "@react-three/drei";
import { useLocation, useNavigate } from "react-router-dom";
import { CameraControls } from "./CameraControls";
import { Vector3 } from "three/src/math/Vector3";
import { AppShell, Button, Container, Group, Image } from "@mantine/core";
import { useFullscreen } from "@mantine/hooks";
import "../../static/styles/main.css";
import HeaderNav from "./HeaderNav";
import PanoNav from "./PanoNav";
import ControlsPanel from "./ControlsPanel";
import Experience from "./Experience";
import HelpDrawer from "./HelpDrawer";
import EmptyPanorama from "./EmptyPanorama";
import { ACESFilmicToneMapping } from "three/src/constants";
import fullscreenIcon from "../../static/svg/full-screen.svg";
import helpIcon from "../../static/svg/help.svg";
import FallbackLoader from "../../components/FallbackLoader";
import ShareModal from "./ShareModal";
import { Share } from "tabler-icons-react";
import ImagePopUp from "./preview/ImagePopUp";
import PanoSpotPopUp from "./preview/PanoSpotPopUp";
import VideoSpotPopUp from "./preview/VideoSpotPopUp";
import ThreedSpotPopUp from "./preview/ThreedSpotPopUp";
import AudioSpotPopUp from "./preview/AudioSpotPopUp";
import "../../index.css";

function Creator() {
	const {
		experience,
		updateExperience,
		changeHotpotPosition,
		changeTextspotPosition,
		changeLinkspotPosition,
		changeVideoPlanePosition,
		changeVideoPlaneRotation,
		changeVideoPlaneScale,
		changeInfoImageSpotPosition,
		changePanoSpotPosition,
		changeVideoSpotPosition,
		changeThreeDSpotPosition,
		changeAudioSpotPosition,
	} = useContext(ExperienceContext);
	const ContextBridge = useContextBridge(ExperienceContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [space, setSpace] = useState(null);
	const [isPreviewComponent, setIsPreviewComponent] = useState(true);

	const toggleComponent = () => {
		setIsPreviewComponent((isPreviewComponent) => !isPreviewComponent);
	};

	useEffect(() => {
		if (location.state?.space?.Experiences) {
			const tempExp = JSON.parse(location?.state?.space.Experiences);
			setSpace(location?.state?.space);
			updateExperience(tempExp.panoramas);
			window.history.replaceState(null, "");
		} else {
			navigate("/dashboard");
		}
	}, []);
	const [opened, setOpened] = useState(false);
	const [activePanoId, setActivePanoId] = useState(experience[0]?.id);
	const [panoramaDetails, setpanoramaDetails] = useState(null);
	const [selectedObject, setSelectedObject] = useState(null);
	const [helpDrawerOpened, setHelpDrawerOpened] = useState(false);
	const [shareModalOpened, setShareModalOpened] = useState(false);
	const [isInfoImageSpotOpen, setIsInfoImageSpotOpen] = useState(false);
	const [infoImageSpotDetails, setInfoImageSpotDetails] = useState({});
	const [isAudioSpotOpen, setIsAudioSpotOpen] = useState(false);
	const [audioSpotDetails, setAudioSpotDetails] = useState({});
	const [isPanoSpotOpen, setIsPanoSpotOpen] = useState(false);
	const [panoSpotDetails, setPanoSpotDetails] = useState({});
	const [isInfoVideoSpotOpen, setIsInfoVideoSpotOpen] = useState(false);
	const [infoVideoSpotDetails, setInfoVideoSpotDetails] = useState({});
	const [isInfoThreeDSpotOpen, setIsInfoThreeDSpotOpen] = useState(false);
	const [infoThreeDSpotDetails, setInfoThreeDSpotDetails] = useState({});
	const [isInfoTextSpotOpen, setIsInfoTextSpotOpen] = useState(false);
	const [infoTextSpotDetails, setInfoTextSpotDetails] = useState("");
	const orbitControl = useRef(null);
	const transformControl = useRef(null);
	const { toggle, fullscreen } = useFullscreen();

	useEffect(() => {
		if (activePanoId !== undefined && experience?.length > 0) {
			const pano = experience.find((pano) => pano.id === activePanoId);
			setpanoramaDetails(pano);
		}
	}, [activePanoId]);
	useEffect(() => {
		if (experience?.length > 0) {
			if (!activePanoId) {
				setActivePanoId(experience[0]?.id);
				return;
			}
		} else {
			setActivePanoId(null);
			setpanoramaDetails(null);
		}
	}, [experience]);
	useEffect(() => {
		if (transformControl.current) closeControls();
	}, [isPreviewComponent]);

	const onTransformControlChange = () => {
		const min = new Vector3(-450, -450, -450);
		const max = new Vector3(450, 450, 450);
		const object = transformControl.current.object;
		if (object) {
			object.position.clamp(min, max);
		}
	};
	const updateObjectTransformation = () => {
		const control = transformControl.current;
		const object = control.object;
		if (object) {
			if (selectedObject.type === "hotspot") {
				changeHotpotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "text") {
				changeTextspotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "link") {
				changeLinkspotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "image") {
				changeInfoImageSpotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "pano") {
				changePanoSpotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "video") {
				changeVideoSpotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "three-d") {
				changeThreeDSpotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "audio") {
				changeAudioSpotPosition(activePanoId, selectedObject.id, [
					object.position.x,
					object.position.y,
					object.position.z,
				]);
			}
			if (selectedObject.type === "inline-video") {
				if (control.mode === "translate") {
					changeVideoPlanePosition(activePanoId, selectedObject.id, [
						object.position.x,
						object.position.y,
						object.position.z,
					]);
				} else if (control.mode === "rotate") {
					changeVideoPlaneRotation(activePanoId, selectedObject.id, [
						object.rotation.x,
						object.rotation.y,
						object.rotation.z,
					]);
				} else if (control.mode === "scale") {
					changeVideoPlaneScale(activePanoId, selectedObject.id, [
						object.scale.x,
						object.scale.y,
						object.scale.z,
					]);
				}
			}
		}
	};
	const closeControls = () => {
		const controls = transformControl.current;
		controls.setMode("translate");
		controls.detach();
		controls.setSize(0);
		setSelectedObject(null);
	};
	const selectObject = (obj, mesh) => {
		if (selectedObject) {
			if (selectedObject.id !== obj.id) {
				setSelectedObject(obj);
				transformControl.current.detach();
				transformControl.current.attach(mesh);
				transformControl.current.setSize(0.5);
				transformControl.current.setMode("translate");
			}
		} else {
			setSelectedObject(obj);
			transformControl.current.detach();
			transformControl.current.attach(mesh);
			transformControl.current.setSize(0.5);
			transformControl.current.setMode("translate");
		}
	};
	return (
		<AppShell
			style={{ backgroundColor: "#040F1F" }}
			navbarOffsetBreakpoint="sm"
			asideOffsetBreakpoint="sm"
			fixed
			padding={0}
			styles={{
				main: { position: "relative" },
			}}
			navbar={
				<PanoNav
					opened={opened}
					activePanoId={activePanoId}
					setActivePanoId={setActivePanoId}
					closeControls={closeControls}
					isPreviewComponent={isPreviewComponent}
				/>
			}
			header={
				<HeaderNav
					opened={opened}
					setOpened={setOpened}
					space={space}
					panoramaDetails={panoramaDetails}
					toggleComponent={toggleComponent}
				/>
			}
		>
			<Suspense fallback={<FallbackLoader />}>
				<Group
					px={0}
					style={{
						position: "absolute",
						top: "0",
						left: "0",
						width: "100%",
						height: "100%",
					}}
				>
					<Canvas
						linear={true}
						camera={{
							position: [0, 0, -0.1],
							fov: 75,
							near: 0.001,
							far: 5000,
						}}
						gl={{
							antialias: true,
							toneMapping: ACESFilmicToneMapping,
						}}
						flat={true}
					>
						<ContextBridge>
							<TransformControls
								ref={transformControl}
								space="local"
								size={0.0}
								onChange={onTransformControlChange}
								onMouseUp={() => {
									orbitControl.current.enabled = true;
									updateObjectTransformation();
								}}
								onMouseDown={() => {
									orbitControl.current.enabled = false;
								}}
							/>
							<CameraControls ref={orbitControl} />
							{panoramaDetails ? (
								<Experience
									activePanoId={activePanoId}
									panoramaDetails={panoramaDetails}
									selectObject={selectObject}
									isPreviewComponent={isPreviewComponent}
									setActivePanoId={setActivePanoId}
									selectedObject={selectedObject}
									setPanoSpotDetails={setPanoSpotDetails}
									setIsPanoSpotOpen={setIsPanoSpotOpen}
									setInfoImageSpotDetails={
										setInfoImageSpotDetails
									}
									setIsInfoImageSpotOpen={
										setIsInfoImageSpotOpen
									}
									setAudioSpotDetails={setAudioSpotDetails}
									setIsAudioSpotOpen={setIsAudioSpotOpen}
									setIsInfoTextSpotOpen={
										setIsInfoTextSpotOpen
									}
									setIsInfoVideoSpotOpen={
										setIsInfoVideoSpotOpen
									}
									setInfoVideoSpotDetails={
										setInfoVideoSpotDetails
									}
									setIsInfoThreeDSpotOpen={
										setIsInfoThreeDSpotOpen
									}
									setInfoThreeDSpotDetails={
										setInfoThreeDSpotDetails
									}
								/>
							) : (
								<EmptyPanorama />
							)}
						</ContextBridge>
					</Canvas>
				</Group>
			</Suspense>
			<ImagePopUp
				infoImageSpotDetails={infoImageSpotDetails}
				isInfoImageSpotOpen={isInfoImageSpotOpen}
				setIsInfoImageSpotOpen={setIsInfoImageSpotOpen}
			/>
			<AudioSpotPopUp
				audioSpotDetails={audioSpotDetails}
				isAudioSpotOpen={isAudioSpotOpen}
				setIsAudioSpotOpen={setIsAudioSpotOpen}
			/>
			<PanoSpotPopUp
				panoSpotDetails={panoSpotDetails}
				isPanoSpotOpen={isPanoSpotOpen}
				setIsPanoSpotOpen={setIsPanoSpotOpen}
			/>
			<VideoSpotPopUp
				infoVideoSpotDetails={infoVideoSpotDetails}
				isInfoVideoSpotOpen={isInfoVideoSpotOpen}
				setIsInfoVideoSpotOpen={setIsInfoVideoSpotOpen}
			/>
			<ThreedSpotPopUp
				infoThreeDSpotDetails={infoThreeDSpotDetails}
				isInfoThreeDSpotOpen={isInfoThreeDSpotOpen}
				setIsInfoThreeDSpotOpen={setIsInfoThreeDSpotOpen}
			/>
			{experience.length > 0 && isPreviewComponent && (
				<div
					className={`component ${
						isPreviewComponent ? "fade-in" : "fade-out"
					}`}
				>
					<ControlsPanel
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						transformControl={transformControl.current}
						orbitControl={orbitControl.current}
						setSelectedObject={setSelectedObject}
						closeControls={closeControls}
					/>
				</div>
			)}
			<HelpDrawer
				opened={helpDrawerOpened}
				setOpened={setHelpDrawerOpened}
			/>
			<ShareModal
				opened={shareModalOpened}
				setOpened={setShareModalOpened}
				id={space?.id}
			/>
			<Group
				style={{
					position: "absolute",
					bottom: "20px",
					right: "20px",
					cursor: "pointer",
				}}
			>
				<Container
					px={0}
					style={{
						cursor: "pointer",
					}}
					onClick={() => setShareModalOpened((o) => !o)}
				>
					<Share size={30} strokeWidth={1} color={"#FFFFFF"} />
					{/* <Image src={Share} /> */}
				</Container>
				<Container
					px={0}
					style={{
						cursor: "pointer",
					}}
					onClick={() => setHelpDrawerOpened((o) => !o)}
				>
					<Image src={helpIcon} width={"30px"} />
				</Container>
				<Container
					px={0}
					style={{
						cursor: "pointer",
					}}
					onClick={toggle}
				>
					<Image src={fullscreenIcon} width={"30px"} />
				</Container>
			</Group>
		</AppShell>
	);
}

export default Creator;
